'use client'

import { Label } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import type { SceneInput } from '@creanizer/types'

interface SceneFormRewriteProps {
  input: SceneInput
  onChange: (input: SceneInput) => void
}

export function SceneFormRewrite({ input, onChange }: SceneFormRewriteProps) {
  // プリセット書き直し指示
  const presetInstructions = [
    { label: '文体を洗練させる', value: '文章をより洗練された文学的な表現に書き直してください。' },
    { label: 'シンプルにする', value: '文章をよりシンプルで読みやすい表現に書き直してください。' },
    { label: '感情を強調', value: 'キャラクターの感情表現をより豊かに、深く書き直してください。' },
    { label: '描写を詳細に', value: '風景や状況の描写をより詳細で視覚的に書き直してください。' },
    { label: '会話を自然に', value: '会話をより自然で生き生きとしたものに書き直してください。' },
    { label: 'テンポを上げる', value: '文章のテンポを上げ、スピード感のある展開に書き直してください。' },
    { label: 'テンポを落とす', value: '文章のテンポを落とし、じっくりと描写するように書き直してください。' }
  ]

  return (
    <div className="space-y-4">
      {/* 元のテキスト */}
      <div>
        <Label htmlFor="originalText">書き直したいテキスト</Label>
        <Textarea
          id="originalText"
          value={input.originalText || ''}
          onChange={(e) => onChange({ ...input, originalText: e.target.value })}
          placeholder="書き直したい元のテキストを貼り付けてください"
          rows={10}
          className="font-mono text-sm"
        />
        <p className="text-xs text-muted-foreground mt-1">
          {input.originalText ? `${input.originalText.length}文字` : '0文字'}
        </p>
      </div>

      {/* 書き直しの指示 */}
      <div>
        <Label htmlFor="rewriteInstruction">書き直しの指示</Label>
        <Textarea
          id="rewriteInstruction"
          value={input.rewriteInstruction || ''}
          onChange={(e) => onChange({ ...input, rewriteInstruction: e.target.value })}
          placeholder="どのように書き直したいか、具体的に指示してください"
          rows={4}
        />
      </div>

      {/* プリセット指示 */}
      <div>
        <Label>プリセット指示（クリックで追加）</Label>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {presetInstructions.map((preset) => (
            <Button
              key={preset.label}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const currentInstruction = input.rewriteInstruction || ''
                const newInstruction = currentInstruction 
                  ? `${currentInstruction}\n${preset.value}`
                  : preset.value
                onChange({ ...input, rewriteInstruction: newInstruction })
              }}
            >
              {preset.label}
            </Button>
          ))}
        </div>
      </div>

      {/* 追加設定 */}
      <div>
        <Label htmlFor="additionalSettings">追加の要望（オプション）</Label>
        <Textarea
          id="additionalSettings"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="その他の詳細な要望や注意点"
          rows={3}
        />
      </div>

      {/* ヒント */}
      <Alert>
        <AlertDescription>
          <strong>リライトモードのコツ：</strong>
          <ul className="mt-2 ml-4 list-disc text-sm">
            <li>具体的な指示を与えるほど、期待通りの結果が得られます</li>
            <li>元の内容の本質は保持されながら、表現が改善されます</li>
            <li>複数のプリセット指示を組み合わせることも可能です</li>
            <li>文体、視点、時制の変更も指示できます</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* 注意事項 */}
      {input.originalText && input.originalText.length > 5000 && (
        <Alert>
          <AlertDescription>
            テキストが長い場合、処理に時間がかかることがあります。
            必要に応じて、セクションごとに分割して処理することをお勧めします。
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}